import { z } from 'zod';

import type { BriefMeasurement } from '@/lib/paid-media/measurement-plan';

export const EvidenceRelationshipSchema = z.enum(['primary', 'supporting', 'counterevidence']);

export const MetaCreativeCoverageSchema = z.object({
  level: z.enum(['recognizable', 'partial', 'unavailable']),
  hasCopy: z.boolean(),
  hasMedia: z.boolean(),
  hasDestination: z.boolean(),
  hasCallToAction: z.boolean(),
  hasStoryIdentity: z.boolean(),
  hasDynamicVariants: z.boolean(),
  limitations: z.array(z.string()),
});

export const AttachOwnedMetaCreativeResponseSchema = z.object({
  status: z.enum(['created', 'reused']),
  commandId: z.string().uuid(),
  evidenceId: z.string().uuid(),
  linkId: z.string().uuid(),
  decisionVersion: z.number().int().positive(),
  evidence: z.object({
    evidenceClass: z.literal('owned_provider_creative'),
    provider: z.literal('meta-ads'),
    providerApiVersion: z.string(),
    selectedResourceId: z.string().uuid(),
    adId: z.string(),
    creativeId: z.string(),
    coverage: MetaCreativeCoverageSchema,
    previewAvailable: z.boolean(),
    performanceState: z.literal('unavailable'),
    retrievedAt: z.string().datetime(),
  }),
});

export type EvidenceRelationship = z.infer<typeof EvidenceRelationshipSchema>;
export type AttachOwnedMetaCreativeResponse = z.infer<typeof AttachOwnedMetaCreativeResponseSchema>;

export interface AttachOwnedMetaCreativeInput {
  connectionId: string;
  selectedResourceId: string;
  adId: string;
  briefId: string;
  relationship: EvidenceRelationship;
  expectedDecisionVersion: number;
  idempotencyKey: string;
  decision: {
    audience?: string | null;
    offer?: string | null;
    hypothesis: string;
    testVariable: string;
    successMeasure?: string | null;
    evidenceLimitations?: string[];
  };
}

export const AttachOwnedGoogleCampaignResponseSchema = z.object({
  outcome: z.enum(['created', 'reused']),
  commandId: z.string().uuid(),
  evidenceId: z.string().uuid(),
  linkId: z.string().uuid(),
  decisionVersion: z.number().int().positive(),
  evidence: z
    .object({
      evidenceClass: z.literal('owned_provider_campaign_outcome'),
      provider: z.literal('google-ads'),
      campaignId: z.string(),
      campaignName: z.string(),
      performanceState: z.enum(['measured', 'unavailable']),
      measuredDays: z.number().int().nonnegative(),
      retrievedAt: z.string(),
    })
    .passthrough(),
});

export type AttachOwnedGoogleCampaignResponse = z.infer<
  typeof AttachOwnedGoogleCampaignResponseSchema
>;

export interface AttachOwnedGoogleCampaignInput {
  connectionId: string;
  customerId: string;
  campaignId: string;
  windowStart: string;
  windowEnd: string;
  briefId: string;
  relationship: EvidenceRelationship;
  expectedDecisionVersion: number;
  idempotencyKey: string;
  decision: {
    audience?: string | null;
    offer?: string | null;
    hypothesis: string;
    testVariable: string;
    channelRole: 'paid' | 'shared_strategy';
    successMeasure?: string | null;
    evidenceLimitations?: string[];
  };
}

export interface BriefEvidenceSource {
  id: string;
  relationship: EvidenceRelationship;
  snapshot: Record<string, unknown>;
  linkedAt: string;
}

export interface BriefEvidenceDecision {
  audience: string | null;
  offer: string | null;
  hypothesis: string | null;
  testVariable: string | null;
  channelRole: 'paid' | 'organic' | 'shared_strategy' | null;
  successMeasure: string | null;
  /** Structured outcome + per-provider capture plan; absent on decisions saved before it existed. */
  measurement?: BriefMeasurement | null;
  evidenceLimitations: string[];
  version: number;
  updatedAt: string;
  /** Who last saved this decision — always a human: the RPC behind every
   * write requires an authenticated caller and no agent tool calls it. */
  updatedBy: string;
}

export interface BriefPriorLearningInput {
  id: string;
  learningId: string;
  decisionVersion: number;
  role: 'auto_brief_context' | 'agent_context' | 'manual_brief_context';
  snapshot: Record<string, unknown>;
  attachedAt: string;
}

export interface BriefEvidenceReadModel {
  sources: BriefEvidenceSource[];
  decision: BriefEvidenceDecision | null;
  priorLearnings?: BriefPriorLearningInput[];
}
